import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { Observable, ObservableInput, of, throwError } from 'rxjs';
import { LocalStorageService } from './web-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AssetsService {
  readonly #KEY_CACHE_PREFIX = 'assets.cache.';
  // czas życia danych w LocalStorage
  readonly #CACHING_MAX_TIME = -1;
  readonly #CACHE_ACTIVE = true;
  readonly #BASE_RESOURCES_PATH = '/assets/';
  // private static readonly BASE_RESOURCES_PATH = '/resources/';

  readonly #http = inject(HttpClient);
  readonly #localStorageService = inject(LocalStorageService);

  getText(assetsRelativeFilePath: string): Observable<string> {
    return this.#getData(assetsRelativeFilePath, this.#extractTextData, 'text');
  }

  getJson(assetsRelativeFilePath: string): Observable<any> {
    return this.#getData(assetsRelativeFilePath, this.#extractJsonData, 'json');
  }

  refreshJson(assetsRelativeFilePath: string): Observable<any> {
    this.clearCachedAsset(assetsRelativeFilePath);
    return this.getJson(assetsRelativeFilePath);
  }

  getBlob(assetsRelativeFilePath: string): Observable<Blob> {
    return this.#getData(assetsRelativeFilePath, this.#extractBlobData, 'blob');
  }

  #getData(
    assetsRelativeFilePath: string,
    mapFn: Function,
    type: string,
  ): Observable<any> {
    let fromCacheObservable = null;

    if (this.#CACHE_ACTIVE) {
      fromCacheObservable = this.#getAssetFromCache(assetsRelativeFilePath);
    }

    if (fromCacheObservable === null) {
      return this.#prepareHttpRequest(assetsRelativeFilePath, type).pipe(
        map((value) => mapFn(value)),
        tap((data) => {
          this.#storeAssetToCache(assetsRelativeFilePath, data);
        }),
      );
    } else {
      return fromCacheObservable;
    }
  }

  #prepareHttpRequest(
    assetsRelativeFilePath: string,
    type: any,
  ): Observable<any> {
    const url = this.#BASE_RESOURCES_PATH + assetsRelativeFilePath;

    return this.#http
      .get(url, {
        params: { timestamp: Date.now().toString() },
        responseType: type,
      })
      .pipe(
        // return this.http.get(url, {params: {timestamp: Date.now().toString()}}).pipe(
        catchError((error: any, caught: Observable<any>) => {
          return this.#handleError(error, caught);
        }),
      );
  }

  #getAssetFromCache(assetsRelativeFilePath: string): Observable<any> | null {
    const cacheKey = this.#prepareCacheKeyFor(assetsRelativeFilePath);
    const dataEntry = <{ date: Date; data: any }>(
      this.#localStorageService.get(cacheKey)
    );
    const hasDataEntry =
      dataEntry?.data !== null && dataEntry?.data !== undefined;

    let isDataExpired;

    if (hasDataEntry) {
      const isDataExpirationActive = this.#CACHING_MAX_TIME > 0;

      if (isDataExpirationActive) {
        const currentTimestamp = new Date().getTime();
        const dataTimestamp = new Date(dataEntry.date).getTime();
        const dataExistingTime = currentTimestamp - dataTimestamp;

        isDataExpired = dataExistingTime >= this.#CACHING_MAX_TIME;
      } else {
        // gdy sprawdzanie terminowowci jest wyłączone to  dana nigdy nie jest przeterminowana
        isDataExpired = false;
      }
    } else {
      isDataExpired = true;
    }

    return !hasDataEntry || isDataExpired ? null : of(dataEntry.data);
  }

  #storeAssetToCache(assetsRelativeFilePath: string, data: any): void {
    const cacheKey = this.#prepareCacheKeyFor(assetsRelativeFilePath);
    this.#localStorageService.set(cacheKey, { date: new Date(), data: data });
  }

  #prepareCacheKeyFor(assetsRelativeFilePath: string): string {
    return this.#KEY_CACHE_PREFIX + assetsRelativeFilePath;
  }

  clearCachedAsset(assetsRelativeFilePath: string): void {
    const cacheKey = this.#prepareCacheKeyFor(assetsRelativeFilePath);
    this.#localStorageService.remove(cacheKey);
  }

  #handleError(
    error: HttpResponse<any>,
    caught: Observable<string>,
  ): ObservableInput<{}> {
    let errMsg: string;
    errMsg = '[' + error.status + ': ' + error.url + '] ' + error.statusText;

    return throwError(() => new Error(errMsg));
  }

  #extractTextData(res: unknown): unknown {
    return res;
  }

  #extractJsonData(res: unknown): unknown {
    return res;
  }

  #extractBlobData(res: unknown): unknown {
    return res;
  }

  //czyszczenie localStorage z wyjątkiem danych zapisanych na tabelach
  clearStorage(): void {
    if (this.#localStorageService.has('tablesData')) {
      const tableData = this.#localStorageService.get('tablesData');
      this.#localStorageService.clear();
      this.#localStorageService.set('tablesData', tableData);
    } else {
      this.#localStorageService.clear();
    }
  }
}
